import { DadosCamposPorRegistro, Registro } from './types/blocoRegistros';
import { Campo } from './types/campo';

export function formatCnpjCpf(numeroRegistro: string): string {
  const numeroRegistroLimpo = numeroRegistro.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  if (numeroRegistroLimpo.length === 11) {
    if (!/^\d+$/.test(numeroRegistroLimpo)) {
      return numeroRegistro;
    }
    return numeroRegistroLimpo.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      '$1.$2.$3-$4',
    );
  } else if (numeroRegistroLimpo.length === 14) {
    return numeroRegistroLimpo.replace(
      /^([a-zA-Z0-9]{2})([a-zA-Z0-9]{3})([a-zA-Z0-9]{3})([a-zA-Z0-9]{4})(\d{2})$/,
      (match, p1, p2, p3, p4, p5) => `${p1}.${p2}.${p3}/${p4}-${p5}`
    );
  } else {
    return numeroRegistro;
  }
}

export function validaCnpj(cnpjOriginal: string): boolean {
  const cnpj = cnpjOriginal.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (cnpj.length !== 14) return false;

  // Se for tudo número igual (ex: 00000000000000), é inválido
  if (/^(\d)\1{13}$/.test(cnpj)) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const getValue = (char: string) => {
    const code = char.charCodeAt(0);
    return code - 48;
  };

  let sum1 = 0;
  for (let i = 0; i < 12; i++) {
    sum1 += getValue(cnpj[i]) * weights1[i];
  }
  const rem1 = sum1 % 11;
  const dv1 = rem1 < 2 ? 0 : 11 - rem1;

  if (dv1 !== parseInt(cnpj[12], 10)) return false;

  let sum2 = 0;
  for (let i = 0; i < 13; i++) {
    sum2 += getValue(cnpj[i]) * weights2[i];
  }
  const rem2 = sum2 % 11;
  const dv2 = rem2 < 2 ? 0 : 11 - rem2;

  if (dv2 !== parseInt(cnpj[13], 10)) return false;

  return true;
}

export function validaCpf(cpfOriginal: string): boolean {
  const cpf = cpfOriginal.replace(/\D/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  let sum1 = 0;
  for (let i = 0; i < 9; i++) {
    sum1 += parseInt(cpf[i], 10) * (10 - i);
  }
  let rem1 = (sum1 * 10) % 11;
  if (rem1 === 10 || rem1 === 11) rem1 = 0;
  if (rem1 !== parseInt(cpf[9], 10)) return false;

  let sum2 = 0;
  for (let i = 0; i < 10; i++) {
    sum2 += parseInt(cpf[i], 10) * (11 - i);
  }
  let rem2 = (sum2 * 10) % 11;
  if (rem2 === 10 || rem2 === 11) rem2 = 0;
  if (rem2 !== parseInt(cpf[10], 10)) return false;

  return true;
}

export const Mascaras = {
  data: '00/00/0000',
};

export function removerCaracteresNaoFiltraveis(filtro: string): string {
  return filtro
    .replace(/[^a-zA-Z0-9\s]/g, '') // Remove caracteres especiais
    .replace(/\s+/g, ' ') // Substitui múltiplos espaços por um único espaço
    .trim(); // Remove espaços no início e no final
}

/**
 * Retorna a máscara de telefone apropriada conforme o número de dígitos.
 * Exemplo:
 * - "11987654321" => "(00) 0 0000-0000"
 * - "1123456789"  => "(00) 0000-0000"
 */
export function getTelefoneMask(numero: string): string {
  const digits = numero.replace(/\D/g, '');
  if (digits.length === 11) {
    return '(00) 0 0000-0000';
  } else {
    return '(00) 0000-0000';
  }
}

export function formatDateForMask(dateStr: string): string {
  if (!dateStr.includes('-')) return dateStr;

  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;

  return `${day}${month}${year}`;
}

/**
 * Aplica uma máscara simples de acordo com o padrão fornecido.
 * Exemplo:
 * applyMask('05111589000119', '00.000.000/0000-00') => '05.111.589/0001-19'
 */
function applyMask(value: string, mask: string): string {
  const isCnpjMask = mask === '00.000.000/0000-00';
  const cleanValue = isCnpjMask 
    ? value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() 
    : value.replace(/\D/g, '');

  let maskedValue = '';
  let digitIndex = 0;

  for (let i = 0; i < mask.length; i++) {
    if (digitIndex >= cleanValue.length) {
      break;
    }
    if (mask[i] === '0') {
      maskedValue += cleanValue[digitIndex];
      digitIndex++;
    } else {
      maskedValue += mask[i];
    }
  }

  return maskedValue;
}

/**
 * Aplica as máscaras nos registros de acordo com os campos recebidos
 * e retorna estrutura pronta para passar ao DynamicDataTable.
 */
export function applyMasksToRegistro(
  dados: DadosCamposPorRegistro,
): DadosCamposPorRegistro {
  const { campos, valores } = dados;

  const camposComMascara = campos.filter((campo: Campo) => campo.mascara);
  const camposDecimais = campos.filter((campo: Campo) => campo.decimal);

  const conteudoMascarado = valores.conteudo.map((registro: Registro) => {
    const novoRegistro = { ...registro };

    camposComMascara.forEach((campo) => {
      const valorOriginal = registro[campo.referencia];

      if (valorOriginal && campo.mascara) {
        try {
          let valorParaMascarar = valorOriginal;
          let mascaraParaAplicar = campo.mascara;

          if (
            campo.mascara === '00/00/0000' &&
            typeof valorOriginal === 'string'
          ) {
            valorParaMascarar = formatDateForMask(valorOriginal);
          }

          if (
            campo.mascara === 'telefone' &&
            typeof valorOriginal === 'string'
          ) {
            mascaraParaAplicar = getTelefoneMask(valorOriginal);
          }

          novoRegistro[campo.referencia] = applyMask(
            valorParaMascarar,
            mascaraParaAplicar,
          );
        } catch {
          novoRegistro[campo.referencia] = valorOriginal;
        }
      } else {
        novoRegistro[campo.referencia] = valorOriginal;
      }
    });

    camposDecimais.forEach((campo) => {
      const valorOriginal = registro[campo.referencia];

      if (valorOriginal && campo.decimal) {
        try {
          novoRegistro[campo.referencia] = formatNumber(
            valorOriginal,
            campo.decimal,
          );
        } catch {
          novoRegistro[campo.referencia] = valorOriginal;
        }
      } else {
        novoRegistro[campo.referencia] = valorOriginal;
      }
    });

    return novoRegistro;
  });

  return {
    campos,
    valores: {
      ...valores,
      conteudo: conteudoMascarado,
    },
  };
}

/**
 * Formata um número para o formato monetário/decimal com separadores brasileiros
 * e um número fixo de casas decimais.
 * @param value O valor numérico a ser formatado.
 * @param decimalPlaces O número de casas decimais fixas (ex: 3).
 * @returns A string formatada (ex: "1.000.000,123").
 */
export function formatNumber(
  value: number | string,
  decimalPlaces: number | null,
): string {
  if (
    typeof value === 'string' &&
    value.trim().length >= 21 &&
    !value.includes(',')
  ) {
    return value;
  }

  const numberValue =
    typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;

  if (isNaN(numberValue)) {
    return '';
  }

  const digitsToUse = numberValue % 1 === 0 ? 0 : decimalPlaces || 0;

  const formatter = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: digitsToUse,
    maximumFractionDigits: digitsToUse,
    useGrouping: true, // Garante o uso do separador de milhar
  });

  return formatter.format(numberValue);
}
